import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { animeList, animeCategories } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = animeCategories.find((c) => c.slug === slug) || animeCategories[0];
  const filteredAnime = animeList.filter((a) => a.categorySlug === slug);

  const pageTitle = `樱花动漫 - ${category.name}分类 - 高清${category.name}动漫在线观看`;
  const pageDesc = `${category.description}。樱花动漫网为您精选${filteredAnime.length}部${category.name}类高清动漫，无需注册即可在线观看。`;
  const pageKeywords = `樱花动漫,${category.name}动漫,高清${category.name},动漫在线观看`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://www.sakura-anime.com/category/${slug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://www.sakura-anime.com/category/${slug}`} />
      <meta property="og:site_name" content="樱花动漫" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />

      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={{
        url: `https://www.sakura-anime.com/category/${slug}`,
        name: `${category.name} - 樱花动漫分类`,
        description: pageDesc,
      }} />

      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]} />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                <i className={`${category.iconClass} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {filteredAnime.length} 部动漫
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <span className="text-sm text-foreground-500 mr-2">其他分类：</span>
              {animeCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-900 border border-background-200/70 hover:border-primary-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredAnime.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                {filteredAnime.map((anime) => (
                  <Link
                    key={anime.id}
                    to={`/article/${anime.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                      <img
                        src={anime.coverImage}
                        alt={anime.title}
                        title={`${anime.title} - ${category.name} 樱花动漫高清在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        {anime.rating}
                      </div>
                      {anime.status === '连载中' && (
                        <div className="absolute top-2 left-2 bg-primary-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                          {anime.status}
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {anime.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{anime.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-foreground-400">{anime.episodes} 话</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                      <span className="text-[11px] text-foreground-400">{anime.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无动漫，请查看其他分类</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  返回首页
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}